import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    InputSignal,
    OnInit,
    output,
    OutputEmitterRef,
    Signal,
    signal,
    untracked,
    WritableSignal
} from "@angular/core";
import {
    OrderItemOptionControlComponent,
    OrderItemOptionLayout
} from "../order-item-option-control/order-item-option-control.component";
import {MenuExtraGroupData, MenuExtraProductData, MenuItem, MenuItemData} from '../../../../menu/menu-entity.model';
import {MenuModel} from '../../../../menu/menu.model';
import {SellableSizesData} from '../../../../menu/sellable-sizes-search.model';
import {ResponsiveDirective} from "../../../../responsive/responsive.directive";
import {ResponsiveService} from "../../../../responsive/responsive.service";
import {NavigationService} from "../../../../navigation/navigation.service";
import {NotificationService} from "../../../../notification/notification.service";
import {QuantityControlComponent} from '../../../../inputs/quantity-control/quantity-control.component';
import {SelectorType} from '../../../../inputs/selector-control/selector';
import {OrderModel} from "../../../order.model";
import {OrderingContextModel} from '../../../../ordering-context/ordering-context.model';
import {OrderCompositeItem, OrderExtraItem, OrderItem, OrderType} from '../../../order';
import {NgClass} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {ToggleComponent} from "../../../../ui-elements/toggle/toggle.component";
import {LinkComponent} from '../../../../buttons/link/link.component';
import {TrimPipe} from '../../../../utils/trim.pipe';
import {ConfigurationModel} from '../../../../configuration/configuration.model';
import {FedeltaImageData} from '@fedelta-pos/data-client';
import {ActivatableDirective} from '../../../../ui-elements/activatable.directive';

@Component({
    selector: "woa-order-item-extras-edit-panel",
    templateUrl: "./order-item-extras-edit-panel.component.html",
    styleUrl: "./order-item-extras-edit-panel.component.scss",
    imports: [
        LinkComponent,
        NgClass,
        OrderItemOptionControlComponent,
        QuantityControlComponent,
        ResponsiveDirective,
        ToggleComponent,
        TranslateModule,
        TrimPipe,
        ActivatableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderItemExtrasEditPanelComponent implements OnInit {
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    public menuModel: MenuModel = inject(MenuModel);
    public orderModel: OrderModel = inject(OrderModel);
    private navigationService: NavigationService = inject(NavigationService);
    private notificationService: NotificationService = inject(NotificationService);
    public responsiveService: ResponsiveService = inject(ResponsiveService);

    MenuModel = MenuModel;
    SelectorType = SelectorType;
    OrderItemOptionLayout = OrderItemOptionLayout;

    public readonly mainOrderItem: InputSignal<OrderItem> = input.required<OrderItem>();
    public readonly orderItem: InputSignal<OrderItem> = input.required<OrderItem>();
    public readonly menuItem: InputSignal<MenuItemData> = input.required<MenuItemData>();
    public readonly originalOrderItem: InputSignal<OrderItem> = input.required<OrderItem>();

    public readonly compositeItem: InputSignal<boolean> = input<boolean>(false);
    public readonly minimalMode: InputSignal<boolean> = input<boolean>(false);
    public readonly promotedExtraGroup: InputSignal<MenuExtraGroupData> = input<MenuExtraGroupData>();
    public readonly showOnlyCombinedExtras: InputSignal<boolean> = input<boolean>(false);
    public readonly allowInitialState: InputSignal<boolean> = input<boolean>(false);

    public onChange: OutputEmitterRef<MenuExtraGroupData> = output<MenuExtraGroupData>();

    public individualExtraGroups: WritableSignal<MenuExtraGroupData[]> = signal<MenuExtraGroupData[]>([]);
    public combinedExtraGroups: WritableSignal<MenuExtraGroupData[]> = signal<MenuExtraGroupData[]>([]);
    public availableExtraGroupCount: WritableSignal<number> = signal<number>(0);
    public availableExtraProducts: WritableSignal<Map<number, MenuExtraProductData[]>> = signal<Map<number, MenuExtraProductData[]>>(new Map());
    public alignAllSingleSelectExtrasForImages: WritableSignal<boolean> = signal<boolean>(false);
    public multiSelectExtraGroupIsAlignedForImages: WritableSignal<Map<number, boolean>> = signal<Map<number, boolean>>(new Map());
    public multiSelectExtraGroupIsCompacted: WritableSignal<Map<number, boolean>> = signal<Map<number, boolean>>(new Map());
    public extraExpanded: WritableSignal<Map<number, boolean>> = signal<Map<number, boolean>>(new Map());
    public extraGroupHasImages: WritableSignal<Map<number, boolean>> = signal<Map<number, boolean>>(new Map());
    public singleSelectExtrasWithSize: WritableSignal<Map<number, Map<number, string>>> = signal<Map<number, Map<number, string>>>(new Map());

    public readonly editing: Signal<boolean> = computed(() => !!this.originalOrderItem());

    private preOrderedExtras: Signal<OrderExtraItem[]> = computed(() => {
        const orderItem: OrderItem = this.orderItem();
        return untracked(() => orderItem?.orderedExtrasSignal());
    });

    public extraTitle: Signal<(extraGroup: MenuExtraGroupData, suffix?: string) => string> = computed(() => {
        const promotedExtraGroup: MenuExtraGroupData = this.promotedExtraGroup();
        const extraGroupHasNoChoice: (extraGroup: MenuExtraGroupData) => boolean = this.menuModel.extraGroupHasNoChoice();
        const extraGroupMaxQty: (extraGroup: MenuExtraGroupData) => number = this.menuModel.extraGroupMaxQty();
        const getSingleOptionExtraLimit: (extraGroup: MenuExtraGroupData) => number = this.menuModel.getSingleOptionExtraLimit();
        return (extraGroup: MenuExtraGroupData, suffix: string = "title"): string => {
            const groupQtyLimit: number = this.menuModel.getExtraGroupMaxQty(extraGroup);
            return promotedExtraGroup && extraGroup.promotionMessage && suffix === "title" ? ("'" + extraGroup.promotionMessage + "'")
                : extraGroup.selectionMessage && suffix === "title" ? ("'" + extraGroup.selectionMessage + "'")
                    : (extraGroupHasNoChoice(extraGroup) ? (extraGroupMaxQty(extraGroup) ? "woa.heading.order.extras.noChoice" : "woa.heading.order.extras.noChoiceNoMax")
                        : this.menuModel.extraGroupIsSingleSelect(extraGroup) ? "woa.heading.order.extras.choose"
                            : this.menuModel.extraGroupHasSingleOption(extraGroup) && getSingleOptionExtraLimit(extraGroup) === 1 ? "woa.heading.order.extras.${verb}Max1Single"
                                : this.menuModel.extraGroupHasSingleOption(extraGroup) && this.menuModel.getExtraGroupMinQty(extraGroup) && getSingleOptionExtraLimit(extraGroup) ? "woa.heading.order.extras.chooseMinMaxSingle"
                                    : this.menuModel.extraGroupHasSingleOption(extraGroup) && this.menuModel.getExtraGroupMinQty(extraGroup) ? "woa.heading.order.extras.chooseMinSingle"
                                        : this.menuModel.getExtraGroupMinQty(extraGroup) && groupQtyLimit ? (this.menuModel.getExtraGroupMinQty(extraGroup) === groupQtyLimit ? "woa.heading.order.extras.${verb}Fixed" : "woa.heading.order.extras.${verb}MinMax")
                                            : this.menuModel.getExtraGroupMinQty(extraGroup) && !groupQtyLimit ? "woa.heading.order.extras.${verb}Min"
                                                : !this.menuModel.getExtraGroupMinQty(extraGroup) && groupQtyLimit ? "woa.heading.order.extras.${verb}Max"
                                                    : "woa.heading.order.extras.${verb}"
                ).replace("${verb}", this.menuModel.extraGroupHasDefaultOptions(extraGroup) ? "change" : "add") + "." + suffix;
        };
    });

    public extraTitleParams: Signal<(extraGroup: MenuExtraGroupData) => any> = computed(() => {
        const extraGroupCount: (extraGroup: MenuExtraGroupData) => number = this.orderItem().extraGroupCount();
        const extraGroupMaxQty: (extraGroup: MenuExtraGroupData) => number = this.menuModel.extraGroupMaxQty();
        return (extraGroup: MenuExtraGroupData): any => ({
            "name": this.menuModel.getExtraGroupDisplayName(extraGroup),
            "count": extraGroupCount(extraGroup) || "{{woa.common.none}}",
            "min": this.menuModel.getExtraGroupMinQty(extraGroup),
            "max": extraGroupMaxQty(extraGroup)
        });
    });

    public getExtraQuantity: Signal<(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData) => number> = computed(() => {
        const orderedExtraForExtraSellableId: (extraId: number, sellableId: number) => OrderExtraItem = this.orderItem().orderedExtraForExtraSellableId();
        return (extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData): number =>
            orderedExtraForExtraSellableId(extraGroup.id, extraProduct.productLine.sellableId)?.quantitySignal() ?? 0;
    });

    public showExtraQuantity: Signal<(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData, showForOrderedOnly?: boolean) => boolean> = computed(() => {
        const getExtraQuantity: (extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData) => number = this.getExtraQuantity();
        return (extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData, showForOrderedOnly: boolean = false): boolean => {
            const qty: number = getExtraQuantity(extraGroup, extraProduct);
            const extraLimit: number = this.menuModel.extraLimit()(extraProduct);
            const groupQtyLimit: number = this.menuModel.getExtraGroupMaxQty(extraGroup);
            return qty > 1 || ((groupQtyLimit == null || groupQtyLimit > 1) && (extraLimit === 0 || extraLimit > 1) && (!showForOrderedOnly || qty > 0));
        };
    });

    // Cannot uncheck a selected extra if it is the only one in the group and the group has a minimum quantity > 0
    public disableMultiSelectCheckbox: Signal<(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData) => boolean> = computed(() => {
        const getExtraQuantity: (extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData) => number = this.getExtraQuantity();
        return (extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData): boolean =>
            this.menuModel.extraGroupHasSingleOption(extraGroup) && this.menuModel.getExtraGroupMinQty(extraGroup) && getExtraQuantity(extraGroup, extraProduct) > 0;
    });

    public showMultiSelectExtraUnavailable: Signal<(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData) => boolean> = computed(() => {
        const getExtraQuantity: (extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData) => number = this.getExtraQuantity();
        const canIncrementExtra: (extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData) => boolean = this.canIncrementExtra();
        return (extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData): boolean =>
            getExtraQuantity(extraGroup, extraProduct) == 0 && !canIncrementExtra(extraGroup, extraProduct);
    });

    public showSingleSelectExtraUnavailable: Signal<(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData) => boolean> = computed(() => {
        const getExtraQuantity: (extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData) => number = this.getExtraQuantity();
        const extraProductAtLimit: (extraProduct: MenuExtraProductData) => boolean = this.extraProductAtLimit();
        return (extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData): boolean =>
            getExtraQuantity(extraGroup, extraProduct) == 0 && extraProductAtLimit(extraProduct);
    });

    public canDecrementExtra: Signal<(extraGroup: MenuExtraGroupData) => boolean> = computed(() => {
        const hasExtraGroupReachedMin: (extraGroup: MenuExtraGroupData) => boolean = this.orderItem().hasExtraGroupReachedMin();
        const extraGroupHasSingleOption: (extraGroup: MenuExtraGroupData) => boolean = this.menuModel.extraGroupHasSingleOption;
        return (extraGroup: MenuExtraGroupData): boolean =>
            extraGroupHasSingleOption(extraGroup) ? !hasExtraGroupReachedMin(extraGroup) : true;
    });

    public canIncrementExtra: Signal<(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData) => boolean> = computed(() => {
        const hasExtraGroupReachedLimit: (extraGroup: MenuExtraGroupData) => boolean = this.orderItem().hasExtraGroupReachedLimit();
        const orderedExtraForExtraSellableId: (extraId: number, sellableId: number) => OrderExtraItem = this.orderItem().orderedExtraForExtraSellableId();
        const extraProductAtLimit: (extraProduct: MenuExtraProductData) => boolean = this.extraProductAtLimit();
        const extraLimit: (extraProduct: MenuExtraProductData) => number = this.menuModel.extraLimit();
        return (extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData): boolean => {
            if (hasExtraGroupReachedLimit(extraGroup) || extraProductAtLimit(extraProduct)) {
                return false;
            }
            let orderExtraItem: OrderExtraItem = orderedExtraForExtraSellableId(extraGroup.id, extraProduct.productLine.sellableId);
            if (orderExtraItem) {
                let extraLimitValue: number = extraLimit(extraProduct);
                return extraLimitValue == 0 || orderExtraItem.quantitySignal() < extraLimitValue;
            }
            return true;
        };
    });

    public showImage: Signal<(extraGroup: MenuExtraGroupData, extraProduct?: MenuExtraProductData) => boolean> = computed(() => {
        const hasImages: Map<number, boolean> = this.extraGroupHasImages();
        const orderedExtraForExtraSellableId: (extraId: number, sellableId: number) => OrderExtraItem = this.orderItem().orderedExtraForExtraSellableId();
        const standardItemImage: (menuItem: MenuItem, useDefault?: boolean) => FedeltaImageData = this.menuModel.standardItemImage();
        const swapLink: (extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData) => string = this.swapLink();
        return (extraGroup: MenuExtraGroupData, extraProduct?: MenuExtraProductData): boolean => {
            if (hasImages.get(extraGroup.id)) {
                return true;
            }
            if (!extraProduct) {
                extraProduct = extraGroup.extraProducts.find((extraProduct: MenuExtraProductData) =>
                    orderedExtraForExtraSellableId(extraGroup.id, extraProduct.productLine.sellableId) != undefined);
            }
            return (extraProduct && standardItemImage(extraProduct.menuItem) && !!swapLink(extraGroup, extraProduct));
        };
    });

    public anyOptionHasPrice: Signal<(extraGroupId: number) => boolean> = computed(() => {
        const products: Map<number, MenuExtraProductData[]> = this.availableExtraProducts();
        return (extraGroupId: number): boolean =>
            !!products.get(extraGroupId).find((extraProduct: MenuExtraProductData) => !!extraProduct.price);
    });

    public swapLink: Signal<(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData) => string> = computed(() => {
        const swapSizes: Map<number, Map<number, string>> = this.singleSelectExtrasWithSize();
        const orderedExtraForExtraSellableId: (extraId: number, sellableId: number) => OrderExtraItem = this.orderItem().orderedExtraForExtraSellableId();
        return (extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData): string =>
            (orderedExtraForExtraSellableId(extraGroup.id, extraProduct.productLine.sellableId) != undefined && swapSizes.has(extraGroup.id))
                ? swapSizes.get(extraGroup.id).get(extraProduct.productLine.sellableId) : undefined;
    });

    public readonly showMultiSelectCheckbox: Signal<(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData) => boolean> = computed(() => {
        const extraLimit: (extraProduct: MenuExtraProductData) => number = this.menuModel.extraLimit();
        return (extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData): boolean =>
            !this.menuModel.extraGroupHasSingleOption(extraGroup) || !this.menuModel.getExtraGroupMinQty(extraGroup) || extraLimit(extraProduct) === 1;
    });

    private readonly extraProductAtLimit: Signal<(extraProduct: MenuExtraProductData) => boolean> = computed(() => {
        if (!this.mainOrderItem()) {
            return () => false;
        }
        const sellableQtyOrdered: (sellableId: number) => number = this.orderItem().sellableQtyOrdered();
        const allowInitialState: boolean = this.allowInitialState();
        const mainOrderItemQuantity: number = this.mainOrderItem().quantitySignal();
        const sellableAtLimit: (sellableId: number, quantum: number, qtyAlreadyOrdered: number, excludeQty?: number) => boolean = this.orderModel.sellableAtLimit();
        const originalSellableQtyOrdered: (sellableId: number) => number = this.originalOrderItem()?.sellableQtyOrdered();

        return (extraProduct: MenuExtraProductData): boolean => {
            const extraQty: number = sellableQtyOrdered(extraProduct.productLine.sellableId);

            // On mobile, a popup is used to edit optional multi-select extras. In this case, we are not working directly on the order item,
            // so removing a product doesn't exclude it from the count, which can lead to the product not being able to be re-added.
            // For this case, we keep track of the extra product quantities on entry, and always allow them to be at least their original value.
            if (allowInitialState && (extraQty < (this.initialQtyOrderedPerSellableId.get(extraProduct.productLine.sellableId) ?? 0))) {
                return false;
            }
            const sellableId: number = extraProduct.menuItem ? extraProduct.menuItem.sellable.sellableId : extraProduct.productLine.sellableId;
            return sellableAtLimit(sellableId, mainOrderItemQuantity, extraQty, originalSellableQtyOrdered?.(sellableId));
        };
    });

    private initialQtyOrderedPerSellableId: Map<number, number> = new Map();
    private assigningCombinedExtras: boolean = false;

    private readonly alignMultiSelectExtrasBetweenGroups: boolean = false;

    constructor() {
        effect(() => {

            // Track all input dependencies that affect which extras are available
            this.orderItem().sellable();
            this.orderItem().availableExtraGroups();
            this.orderItem().orderedExtrasSignal();
            this.menuItem();
            this.minimalMode();
            this.showOnlyCombinedExtras();
            this.promotedExtraGroup();
            this.configurationModel.app();
            this.menuModel.categories();
            this.responsiveService.mode();

            // Execute the method without tracking any signal reads inside it
            untracked(() => this.loadAvailableExtras());
        });
    }

    ngOnInit(): void {
        if (this.allowInitialState()) {
            this.initialQtyOrderedPerSellableId = new Map(this.orderItem().qtyOrderedPerSellableId());
        }
    }

    private loadAvailableExtras(): void {
        if (!(this.orderItem() && this.menuItem() && this.configurationModel.app())) {
            return;
        }
        this.menuModel.categories(); // Reload if menu is rebuilt

        const prevAvailableExtraProducts: Map<number, MenuExtraProductData[]> = this.availableExtraProducts();
        const additionalDefaultableExtraProducts: MenuExtraProductData[] = [];

        this.combinedExtraGroups.set([]);
        this.availableExtraGroupCount.set(0);
        this.availableExtraProducts.set(new Map());
        this.extraGroupHasImages.set(new Map());
        this.singleSelectExtrasWithSize.set(new Map());
        this.alignAllSingleSelectExtrasForImages.set(false);
        this.multiSelectExtraGroupIsAlignedForImages.set(new Map());
        this.multiSelectExtraGroupIsCompacted.set(new Map());

        const firstTierExtraGroups: MenuExtraGroupData[] = [];
        const secondTierExtraGroups: MenuExtraGroupData[] = [];
        // noinspection JSMismatchedCollectionQueryUpdate
        let extraGroupTier: MenuExtraGroupData[];

        let anyNormalMultiSelectExtraGroupHasImages: boolean = false;
        let anyCompactMultiSelectExtraGroupHasImages: boolean = false;

        let itemsRemoved: boolean = false;

        let lastParentExtraGroupId: number = 0;
        let skippingParentGroup: boolean = false;

        this.orderItem().availableExtraGroups().forEach((extraGroup: MenuExtraGroupData) => {
            const parentExtraGroupId: number = this.menuModel.getParentExtraGroupId(extraGroup, this.orderItem().sellable());
            const isParentGroup: boolean = !parentExtraGroupId;
            const isChildGroup: boolean = parentExtraGroupId === lastParentExtraGroupId;
            if (isParentGroup) {
                lastParentExtraGroupId = extraGroup.id;
            }

            // Extra groups are skipped completely if:
            // - They are associated with a parent group which is being skipped; or
            // - The group is not visible (zero limit, single default option); or
            // - We are only showing combined extras (popup); or
            // - This is a promoted extra popup (for a different group).
            if ((skippingParentGroup && isChildGroup && extraGroup !== this.promotedExtraGroup()) ||
                !this.menuModel.extraGroupIsVisible(extraGroup) ||
                (this.showOnlyCombinedExtras() && !isChildGroup && (this.menuModel.extraGroupIsSingleSelect(extraGroup) || !this.menuModel.extraGroupIsOptional(extraGroup))) ||
                (this.promotedExtraGroup() && !isChildGroup && extraGroup !== this.promotedExtraGroup())) {
                if (isParentGroup) {
                    skippingParentGroup = true;
                }
                return;
            }
            skippingParentGroup = false;

            const prevAvailableExtraProductsForGroup: MenuExtraProductData[] = prevAvailableExtraProducts.get(extraGroup.id) || [];

            // Extra groups are also skipped if they are conditionally unavailable. In which case, any selected extras for the group are also removed.
            if (!this.orderItem().isExtraGroupConditionallyAvailable(extraGroup)) {
                prevAvailableExtraProductsForGroup.forEach((prevProduct: MenuExtraProductData) => {
                    if (this.orderItem().removeOrderExtraProduct(extraGroup, prevProduct)) {
                        itemsRemoved = true;
                    }
                });
                return;
            }

            let groupShowsExtraQuantities: boolean = false;

            // Selected vs unselected lists are used for managing order of multi-select extras.
            let selectedExtraProducts: MenuExtraProductData[] = [];
            let unselectedExtraProducts: MenuExtraProductData[] = [];

            let excludedSellableIds: Map<number, boolean> = new Map();
            let sellableIdsWithSize: Map<number, string> = new Map();

            // Find the available products for the extra group
            extraGroup.extraProducts.forEach((extraProduct: MenuExtraProductData) => {
                if (excludedSellableIds.get(extraProduct.productLine.sellableId)) {
                    return;
                }
                if (!this.orderItem().isExtraProductAvailable(extraProduct, this.menuItem())) {
                    return;
                }

                if (this.menuModel.extraGroupIsSingleSelect(extraGroup)) {

                    // For single-select extras in size groups, only one option is displayed at a time.  We need to work out here which one to display.
                    // This may also require adding the extra product to the order if it's the only option.
                    let sizes: SellableSizesData[] = this.menuModel.extraSizes()(extraProduct.productLine.sellableId, extraGroup);
                    if (sizes && sizes.length) {
                        let extraSellableIdToAdd: number;

                        // Try to find if any of the items are already added to the order item
                        let selectedSizeExtra: SellableSizesData = sizes.find((sellableSizes: SellableSizesData) => {
                            let extraProduct: MenuExtraProductData = extraGroup.extraProducts.find((extraProduct: MenuExtraProductData) => extraProduct.productLine.sellableId === sellableSizes.sellableId);
                            return extraProduct && this.orderItem().orderedExtrasSignal().find((extraItem: OrderExtraItem) => extraItem.extraGroup.id === extraGroup.id &&
                                extraItem.extraProduct.productLine.sellableId === extraProduct.productLine.sellableId) != undefined;
                        });
                        if (selectedSizeExtra) {
                            extraSellableIdToAdd = selectedSizeExtra.sellableId;
                        } else {

                            // Use the standard size sellable by default...
                            extraSellableIdToAdd = sizes[0].standardSellableId;

                            // ...but try to find the same size as the order item if possible
                            const groupId: number = sizes[0].sellableSizeGroupId;
                            let sellableSizeId: number = this.menuModel.findSellableSizeId()(groupId, this.orderItem().sellable().sellableId);
                            if (sellableSizeId) {
                                let sameSize: SellableSizesData = sizes.find((sellableSizes: SellableSizesData) => sellableSizes.sellableSizeId === sellableSizeId);
                                if (sameSize) {
                                    extraSellableIdToAdd = sameSize.sellableId;
                                }
                            }
                        }

                        let addExtraProduct: MenuExtraProductData = extraGroup.extraProducts.find((extraProduct: MenuExtraProductData) => extraProduct.productLine.sellableId === extraSellableIdToAdd);
                        if (!this.orderItem().isExtraProductAvailable(addExtraProduct, this.menuItem())) {
                            return;
                        }

                        if (addExtraProduct) {

                            // If all the products in the extra are in the size group, select the chosen extra rather than leaving a single option unselected.
                            if (!selectedSizeExtra && sizes.length === extraGroup.extraProducts.length) {
                                this.orderItem().addOrderExtraItem(this.orderItem().createOrderExtraItem(extraGroup, addExtraProduct));
                            }

                            selectedExtraProducts.push(addExtraProduct);
                            sellableIdsWithSize.set(addExtraProduct.productLine.sellableId, this.menuModel.getSizeName(sizes));

                            // Other sizes are excluded (only one shown at once)
                            sizes.forEach((size: SellableSizesData) => {
                                excludedSellableIds.set(size.sellableId, true);
                            });
                        } else {
                            selectedExtraProducts.push(extraProduct);
                        }
                    } else {
                        if (this.menuModel.standardItemImage()(extraProduct.menuItem)) {
                            this.extraGroupHasImages().set(extraGroup.id, true);
                            this.alignAllSingleSelectExtrasForImages.set(this.responsiveService.desktop());
                        }
                        selectedExtraProducts.push(extraProduct);
                    }
                } else {
                    if (extraProduct.menuItem) {
                        if (this.menuModel.standardItemImage()(extraProduct.menuItem)) {
                            this.extraGroupHasImages().set(extraGroup.id, true);
                        }
                        if (this.configurationModel.app()?.compactExtrasThreshold && !groupShowsExtraQuantities && this.showExtraQuantity()(extraGroup, extraProduct)) {
                            groupShowsExtraQuantities = true;
                        }
                    } else if (this.menuModel.getExtraProductImage(extraProduct)) {
                        this.extraGroupHasImages().set(extraGroup.id, true);
                    }

                    if (this.configurationModel.app()?.showSelectedExtrasFirst) {
                        if (this.preOrderedExtras().some((orderedExtra: OrderExtraItem) =>
                            orderedExtra.extraGroup.id === extraGroup.id && orderedExtra.extraProduct.productLine.sellableId === extraProduct.productLine.sellableId)) {
                            selectedExtraProducts.push(extraProduct);
                        } else {
                            unselectedExtraProducts.push(extraProduct);
                        }
                    } else {
                        selectedExtraProducts.push(extraProduct);
                    }
                }
            });

            const newAvailableExtraProductsForGroup: MenuExtraProductData[] = selectedExtraProducts.concat(unselectedExtraProducts);
            this.availableExtraProducts().set(extraGroup.id, newAvailableExtraProductsForGroup);
            if (this.availableExtraProducts().get(extraGroup.id).length) {
                this.availableExtraGroupCount.set(this.availableExtraGroupCount() + 1);
                if (this.menuModel.extraGroupIsSingleSelect(extraGroup)) {
                    if (!isChildGroup) {
                        extraGroupTier = firstTierExtraGroups;
                    }
                    if (sellableIdsWithSize.size) {
                        this.singleSelectExtrasWithSize().set(extraGroup.id, sellableIdsWithSize);
                    }
                } else if (this.minimalMode() && this.menuModel.extraGroupIsOptional(extraGroup)) {
                    if (!isChildGroup) {
                        extraGroupTier = this.combinedExtraGroups();
                    }
                } else {
                    if (!isChildGroup) {
                        extraGroupTier = secondTierExtraGroups;
                    }
                    this.multiSelectExtraGroupIsAlignedForImages().set(extraGroup.id, false);
                    if ((!groupShowsExtraQuantities || this.responsiveService.mobile()) && this.availableExtraProducts().get(extraGroup.id).length >= this.configurationModel.app()?.compactExtrasThreshold) {
                        this.multiSelectExtraGroupIsCompacted().set(extraGroup.id, true);
                        if (this.extraGroupHasImages().get(extraGroup.id)) {
                            anyCompactMultiSelectExtraGroupHasImages = true;
                        }
                    } else if (this.extraGroupHasImages().get(extraGroup.id)) {
                        anyNormalMultiSelectExtraGroupHasImages = true;
                    }
                }
                if (extraGroupTier === undefined) {
                    extraGroupTier = firstTierExtraGroups;
                }
                extraGroupTier.push(extraGroup);
            } else if (isParentGroup) {
                skippingParentGroup = true;
            }

            // Check for conditional extras which have become available due to other extras having been selected, and which may need to be defaulted.
            // Don't default items from single-select groups if there is already an extra item from that group selected.
            // Don't default items from the combined extra groups if we are currently assigning them from the popup.
            if (!(this.assigningCombinedExtras && extraGroupTier === this.combinedExtraGroups())) {
                extraGroup.extraProducts.forEach((extraProduct: MenuExtraProductData) => {
                    if (prevAvailableExtraProducts.size && extraProduct.defaultOrderQty && prevAvailableExtraProductsForGroup.indexOf(extraProduct) < 0 &&
                        !(this.menuModel.extraGroupIsSingleSelect(extraGroup) && this.orderItem().orderedExtrasSignal().find((extraItem: OrderExtraItem) => extraItem.extraGroup.id === extraGroup.id) !== undefined)) {
                        additionalDefaultableExtraProducts.push(extraProduct);
                    }
                });
            }

            // Check for any previously available extra products which have become unavailable.
            prevAvailableExtraProductsForGroup.forEach((prevProduct: MenuExtraProductData) => {
                if (newAvailableExtraProductsForGroup.indexOf(prevProduct) < 0 && this.orderItem().removeOrderExtraProduct(extraGroup, prevProduct)) {
                    itemsRemoved = true;
                }
            });
        });

        this.multiSelectExtraGroupIsAlignedForImages().forEach((value, extraId: number) =>
            this.multiSelectExtraGroupIsAlignedForImages().set(extraId,
                !this.alignMultiSelectExtrasBetweenGroups ? this.extraGroupHasImages().get(extraId)
                    : this.multiSelectExtraGroupIsCompacted().get(extraId) ? anyCompactMultiSelectExtraGroupHasImages
                        : anyNormalMultiSelectExtraGroupHasImages));

        this.individualExtraGroups.set(firstTierExtraGroups.concat(secondTierExtraGroups));

        this.individualExtraGroups().forEach((extraGroup: MenuExtraGroupData) => {
            if (this.menuModel.extraGroupIsSingleSelect(extraGroup)) {
                if (this.extraExpanded().get(extraGroup.id) === undefined) {
                    this.extraExpanded().set(extraGroup.id, true);
                }
            } else if (this.extraExpanded().get(extraGroup.id) === undefined) {
                this.extraExpanded().set(extraGroup.id,
                    this.availableExtraGroupCount() === 1 ||
                    !!this.menuModel.getExtraGroupMinQty(extraGroup) ||
                    !this.configurationModel.app().collapseExtras ||
                    this.menuModel.extraGroupHasSingleOption(extraGroup) ||
                    !!this.orderItem().orderedExtrasSignal().find((orderExtraItem: OrderExtraItem) => orderExtraItem.extraGroup === extraGroup));
            }
        });

        // Recreate Maps to trigger change detection
        this.availableExtraProducts.set(new Map(this.availableExtraProducts()));
        this.extraGroupHasImages.set(new Map(this.extraGroupHasImages()));
        this.singleSelectExtrasWithSize.set(new Map(this.singleSelectExtrasWithSize()));
        this.multiSelectExtraGroupIsAlignedForImages.set(new Map(this.multiSelectExtraGroupIsAlignedForImages()));
        this.multiSelectExtraGroupIsCompacted.set(new Map(this.multiSelectExtraGroupIsCompacted()));
        this.extraExpanded.set(new Map(this.extraExpanded()));

        if (itemsRemoved) {
            this.orderModel.notifyOrderChange();
        }

        // If any defaultable extras have become available, add them (if possible) and reload extras since there could be further dependencies.
        // Also reload if items have been removed.
        if (itemsRemoved ||
            (additionalDefaultableExtraProducts.length && this.orderItem().addDefaultExtras(this.compositeItem() ? <OrderCompositeItem>this.mainOrderItem() : undefined, additionalDefaultableExtraProducts))) {
            this.loadAvailableExtras();
        }
    }

    public selectSingleSelectExtra(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData, index: number, option?: OrderItemOptionControlComponent, body?: HTMLElement): boolean {
        if (this.orderItem().orderedExtraForExtraSellableId()(extraGroup.id, extraProduct.productLine.sellableId) != undefined) {
            if (!!this.swapLink()(extraGroup, extraProduct)) {
                void this.swapExtra(extraGroup, extraProduct, index);
            }
            return false;
        }

        if (this.extraProductAtLimit()(extraProduct)) {
            if (this.menuModel.itemOutOfStock()(extraProduct.menuItem)) {
                void this.notificationService.displayAlert(null, "woa.message.order.productOutOfStock", {item: this.menuModel.getExtraProductDisplayName(extraProduct)});
            } else if (this.extraProductAtLimit()(extraProduct)) {
                void this.notificationService.displayAlert(null, "woa.message.order.productLimitReached", {item: this.menuModel.getExtraProductDisplayName(extraProduct)});
            }
            return false;
        }

        this.orderItem().orderedExtrasSignal().find((orderedExtra: OrderExtraItem) => {
            if (extraGroup.id === orderedExtra.extraGroup.id) {
                this.orderItem().removeOrderExtraItem(orderedExtra.identifier);
                return true;
            }
            return false;
        });
        this.orderItem().addOrderExtraItem(this.orderItem().createOrderExtraItem(extraGroup, extraProduct), this.editing());

        this.onChange.emit(extraGroup);
        if (body) {
            this.setExtraBodyHeight(extraGroup, body);
        }
        return true;
    }

    public selectMultiSelectExtra(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData, body: HTMLElement): boolean {
        if (this.showMultiSelectExtraUnavailable()(extraGroup, extraProduct)) {
            if (this.menuModel.itemOutOfStock()(extraProduct.menuItem)) {
                void this.notificationService.displayAlert(null, "woa.message.order.productOutOfStock", {item: this.menuModel.getExtraProductDisplayName(extraProduct)});
            } else if (this.extraProductAtLimit()(extraProduct)) {
                void this.notificationService.displayAlert(null, "woa.message.order.productLimitReached", {item: this.menuModel.getExtraProductDisplayName(extraProduct)});
            }
            return false;
        }

        let orderExtraItem: OrderExtraItem = this.orderItem().orderedExtraForExtraSellableId()(extraGroup.id, extraProduct.productLine.sellableId);
        if (orderExtraItem) {
            if (this.menuModel.extraGroupHasSingleOption(extraGroup) && this.menuModel.getExtraGroupMinQty(extraGroup)) {
                return false;
            }
            this.orderItem().removeOrderExtraItem(orderExtraItem.identifier, this.editing());
        } else {
            if (this.orderItem().hasExtraGroupReachedLimit()(extraGroup)) {
                void this.notificationService.displayAlert(null, "woa.message.order.qtyLimitReached", {item: this.menuModel.getExtraGroupDisplayName(extraGroup)});
                return false;
            }
            this.orderItem().addOrderExtraItem(this.orderItem().createOrderExtraItem(extraGroup, extraProduct), this.editing());
        }
        this.onChange.emit(extraGroup);
        this.setExtraBodyHeight(extraGroup, body);
        return true;
    }

    public toggleExtra(extraGroup: MenuExtraGroupData, body: HTMLElement, group: HTMLElement): void {
        if (this.promotedExtraGroup()) {
            return;
        }
        this.extraExpanded().set(extraGroup.id, !this.extraExpanded().get(extraGroup.id));
        this.extraExpanded.set(new Map(this.extraExpanded()));
        this.setExtraBodyHeight(extraGroup, body, group);
    }

    // noinspection JSUnusedLocalSymbols
    private setExtraBodyHeight(extraGroup: MenuExtraGroupData, body: HTMLElement, group?: HTMLElement): void {
        body.style.maxHeight = (body.scrollHeight + 1) + "px";
        setTimeout(() => {
            const expanded: boolean = this.extraExpanded().get(extraGroup.id);
            body.style.maxHeight = expanded ? (body.scrollHeight + 1) + "px" : "0";
            /*
            if (expanded && group) {
                setTimeout(() => group.scrollIntoView({
                    block: "center",
                    inline: "nearest",
                    behavior: "smooth"
                }), 330);
            }
            */
        });
    }

    public decrementExtra(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData, body: HTMLElement): void {
        let orderExtraItem: OrderExtraItem = this.orderItem().orderedExtraForExtraSellableId()(extraGroup.id, extraProduct.productLine.sellableId);
        if (orderExtraItem) {
            orderExtraItem.decrementQuantity();
            if (orderExtraItem.quantitySignal() === 0) {
                this.orderItem().removeOrderExtraItem(orderExtraItem.identifier, this.editing());
            }
        }
        this.onChange.emit(extraGroup);
        this.setExtraBodyHeight(extraGroup, body);
    }

    public incrementExtra(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData, body: HTMLElement): void {
        let orderExtraItem: OrderExtraItem = this.orderItem().orderedExtraForExtraSellableId()(extraGroup.id, extraProduct.productLine.sellableId);
        if (!orderExtraItem) {
            this.selectMultiSelectExtra(extraGroup, extraProduct, body);
        } else {
            if (this.orderItem().hasExtraGroupReachedLimit()(extraGroup)) {
                void this.notificationService.displayAlert(null, "woa.message.order.qtyLimitReached", {item: this.menuModel.getExtraGroupDisplayName(extraGroup)});
                return;
            }
            if (this.menuModel.itemOutOfStock()(extraProduct.menuItem)) {
                void this.notificationService.displayAlert(null, "woa.message.order.productOutOfStock", {item: this.menuModel.getExtraProductDisplayName(extraProduct)});
                return;
            } else if (this.extraProductAtLimit()(extraProduct)) {
                void this.notificationService.displayAlert(null, "woa.message.order.productLimitReached", {item: this.menuModel.getExtraProductDisplayName(extraProduct)});
                return;
            }
            if (this.canIncrementExtra()(extraGroup, extraProduct)) {
                let orderExtraItem: OrderExtraItem = this.orderItem().orderedExtraForExtraSellableId()(extraGroup.id, extraProduct.productLine.sellableId);
                orderExtraItem.incrementQuantity();
            }
            this.onChange.emit(extraGroup);
            this.setExtraBodyHeight(extraGroup, body);
        }
    }

    public async swapExtra(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData, index: number, event?: Event): Promise<void> {
        event?.stopPropagation();
        let sizes: SellableSizesData[] = this.menuModel.extraSizes()(extraProduct.productLine.sellableId, extraGroup);
        let extraProductsToSelect: MenuExtraProductData[] = sizes
            .map((size) => extraGroup.extraProducts.find((extraProduct: MenuExtraProductData) => extraProduct.productLine.sellableId === size.sellableId))
            .filter((extraProduct: MenuExtraProductData) => {
                if (extraProduct.menuItem) {
                    return (!(extraProduct.menuItem.hidden ||
                        (this.orderingContextModel.currentStore().orderAheadStore && !this.menuModel.extraItemIsAvailableForOrderType()(extraProduct.menuItem, OrderType.ORDER_AHEAD)) ||
                        (this.orderingContextModel.currentStore().orderAtTableStore && !this.menuModel.extraItemIsAvailableForOrderType()(extraProduct.menuItem, OrderType.ORDER_AT_TABLE)) ||
                        !this.menuModel.extraItemIsAvailableForFilters(extraProduct.menuItem, this.menuItem(), this.orderItem().selectedFilterTags())));
                }
                return !this.menuItem().filterExtras;
            });

        const selectedExtra: MenuExtraProductData = await this.navigationService.createPage("woa-order-item-extra-sizes-edit-page", {
            orderItem: this.orderItem(),
            extra: extraGroup,
            availableExtraProducts: extraProductsToSelect,
            selectedExtraProduct: extraProduct,
            groupName: this.menuModel.getSizeName(sizes)
        });
        if (selectedExtra && selectedExtra.productLine.sellableId !== extraProduct.productLine.sellableId) {
            if (this.selectSingleSelectExtra(extraGroup, selectedExtra, index)) {
                this.availableExtraProducts().get(extraGroup.id)[index] = selectedExtra;
                this.availableExtraProducts.set(new Map(this.availableExtraProducts()));
                this.singleSelectExtrasWithSize().get(extraGroup.id).set(selectedExtra.productLine.sellableId, this.menuModel.getSizeName(sizes));
                this.singleSelectExtrasWithSize.set(new Map(this.singleSelectExtrasWithSize()));
            }
        }
    }

    public async chooseCombinedExtras(event: MouseEvent): Promise<void> {
        event?.stopPropagation();
        let editOrderItem: OrderItem = this.orderItem().createClone();
        if (await this.navigationService.createPage("woa-order-item-combined-extras-edit-page", {
            orderItem: editOrderItem,
            mainOrderItem: this.mainOrderItem(),
            originalOrderItem: this.originalOrderItem(),
            menuItem: this.menuItem()
        })) {
            this.assigningCombinedExtras = true;
            this.orderItem().assignExtras(editOrderItem, undefined, true);
            this.combinedExtraGroups().forEach((extraGroup: MenuExtraGroupData) => this.onChange.emit(extraGroup));
            this.assigningCombinedExtras = false;
        }
    }
}
